import styled from 'styled-components';
import * as animations from '../../styles/animations';

export const FeaturesSection = styled.section<{ $isVisible?: boolean }>`
  padding: 120px 0;
  background: linear-gradient(180deg, #0a0a0a 0%, #0a0a0a 100%);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 200px;
    background: linear-gradient(180deg, transparent 0%, rgba(10, 10, 10, 0.2) 20%, rgba(10, 10, 10, 0.5) 40%, rgba(10, 10, 10, 0.8) 70%, rgba(10, 10, 10, 1) 100%);
    pointer-events: none;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

export const PowerfulFeaturesContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  margin-top: 80px;
  align-items: start;

  @media (max-width: 1200px) {
    gap: 60px;
  }

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-top: 32px;
    align-items: center;
  }
`;

export const FeaturedDisplayContainer = styled.div`
  position: sticky;
  top: 120px;
  @media (max-width: 768px) {
    position: static;
    top: auto;
  }
`;

export const FeaturedCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: ${props => props.theme.borders.radius.xl};
  padding: 48px;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(20px);
  margin: 0 auto;
  max-width: 420px;
  width: 100%;

  @media (max-width: 768px) {
    margin: 0 auto 0 auto;
    max-width: 98vw;
    width: 100%;
    top: 0;
    left: 0;
    right: 0;
    transform: none;
    animation: none;
    will-change: auto;
  }

  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, #ff4444, #ff2222, #ff1111, #ff4444);
    border-radius: ${props => props.theme.borders.radius.xl};
    opacity: 0.1;
    z-index: -1;
    animation: borderGlow 3s ease-in-out infinite;
  }

  @keyframes borderGlow {
    0%, 100% { opacity: 0.1; }
    50% { opacity: 0.3; }
  }
`;

export const FeaturedIcon = styled.div<{ $active: boolean }>`
  width: 80px;
  height: 80px;
  border-radius: ${props => props.theme.borders.radius.xl};
  background: linear-gradient(135deg, rgba(255, 68, 68, 0.2), rgba(255, 68, 68, 0.05));
  border: 2px solid rgba(255, 68, 68, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
  color: #ff4444;
  transform: ${props => props.$active ? 'scale(1.05)' : 'scale(1)'};
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);

  svg {
    width: 40px;
    height: 40px;
    filter: drop-shadow(0 4px 12px rgba(255, 68, 68, 0.4));
    transition: all 0.4s ease;
  }
`;

export const FeaturedContent = styled.div`
  transition: all 0.4s ease;
  animation: fadeInContent 0.5s ease-in-out;

  @keyframes fadeInContent {
    0% {
      opacity: 0;
      transform: translateY(10px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const FeaturedTitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  color: #fafafa;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #fafafa 0%, #a1a1aa 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  transition: all 0.3s ease;
  opacity: 1;
`;

export const FeaturedDescription = styled.p`
  font-size: 1.125rem;
  color: #a0a0a0;
  line-height: 1.6;
  margin-bottom: 20px;
  transition: all 0.3s ease;
  opacity: 1;
`;

export const FeaturedDetail = styled.p`
  font-size: 1rem;
  color: #ff6666;
  font-weight: 500;
  padding: 16px 20px;
  background: rgba(255, 68, 68, 0.1);
  border-left: 3px solid #ff4444;
  border-radius: 0 8px 8px 0;
  margin: 0;
  transition: all 0.3s ease;
  opacity: 1;
`;

export const FeaturedAnimation = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
`;

export const AnimatedBorder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, #ff4444, transparent);
  animation: borderScan 4s ease-in-out infinite;

  @keyframes borderScan {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

export const FeaturesListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const FeatureListItem = styled.div<{ $active: boolean; $hovered: boolean; $delay: number }>`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px;
  border-radius: ${props => props.theme.borders.radius.lg};
  background: ${props => props.$active || props.$hovered ? 'rgba(255, 68, 68, 0.05)' : 'rgba(255, 255, 255, 0.02)'};
  border: 1px solid ${props => props.$active || props.$hovered ? 'rgba(255, 68, 68, 0.2)' : 'rgba(255, 255, 255, 0.06)'};
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${props => props.$hovered ? 'translateX(8px)' : 'translateX(0)'};
  opacity: 0;
  animation: ${animations.slideInUp} 0.7s cubic-bezier(0.25, 0.1, 0.25, 1) forwards;
  animation-delay: ${props => props.$delay}ms;

  &:hover {
    background: rgba(255, 68, 68, 0.08);
    border-color: rgba(255, 68, 68, 0.3);
    transform: translateX(12px);
  }
`;

export const FeatureNumber = styled.div<{ $active: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.$active ? 'linear-gradient(135deg, #ff4444, #ff2222)' : 'rgba(255, 255, 255, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: ${props => props.$active ? 'white' : '#71717a'};
  font-size: 0.9rem;
  flex-shrink: 0;
  transition: all 0.4s ease;
`;

export const FeatureListContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const FeatureListIcon = styled.div<{ $active: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: ${props => props.theme.borders.radius.md};
  background: ${props => props.$active ? 'rgba(255, 68, 68, 0.15)' : 'rgba(255, 255, 255, 0.05)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$active ? '#ff4444' : '#71717a'};
  flex-shrink: 0;
  transition: all 0.4s ease;

  svg {
    width: 24px;
    height: 24px;
  }
`;

export const FeatureListText = styled.div`
  flex: 1;
`;

export const FeatureListTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: #fafafa;
  margin: 0 0 4px 0;
`;

export const FeatureListDesc = styled.p`
  font-size: 0.9rem;
  color: #a0a0a0;
  margin: 0;
  line-height: 1.4;
`;

export const FeatureArrow = styled.div<{ $active: boolean }>`
  width: 20px;
  height: 20px;
  color: ${props => props.$active ? '#ff4444' : '#71717a'};
  flex-shrink: 0;
  transition: all 0.4s ease;
  transform: ${props => props.$active ? 'translateX(4px)' : 'translateX(0)'};

  svg {
    width: 100%;
    height: 100%;
  }
`;
